import { Type } from 'class-transformer';
import {
	IsArray,
	IsEnum,
	IsInt,
	IsOptional,
	IsString,
	ValidateNested,
} from 'class-validator';
import { Priority } from '../../enums';
import {
	ClientIssueInterface,
	IssueInterface,
	IssueStatus,
	IssueType,
	VendorIssueInterface,
} from '../database';

class IssueDto implements Partial<IssueInterface> {
	@IsOptional()
	@IsString()
	public name?: string;

	@IsOptional()
	@IsString()
	public description?: string | null;

	// PD-16475 — rich text from the CRM editor. When sent, the server
	// sanitizes it and derives `description` from it; `description` alone (an
	// AI tool, a web form) replaces the rich copy with plain text.
	@IsOptional()
	@IsString()
	public descriptionHtml?: string | null;

	@IsOptional()
	@IsEnum(IssueStatus)
	public status?: IssueStatus;

	@IsOptional()
	@IsEnum(IssueType)
	public type?: IssueType | null;

	@IsOptional()
	@IsEnum(Priority)
	public priority?: Priority;

	@IsOptional()
	@IsInt()
	@Type(() => Number)
	public assigneeUserId?: number | null;

	// PD-13705 — provenance FK set by the portal submission endpoint; the
	// CRM issue form never sends it.
	@IsOptional()
	@IsInt()
	@Type(() => Number)
	public submitterPublicUserId?: number | null;

	// PD-16475 — the company picked on the CRM form. Optional: omitted on
	// create it is derived server-side (client's primary company, else the
	// creator's only company), and omitted on update it is left unchanged.
	// Always validated against the companies the caller may choose from.
	@IsOptional()
	@IsInt()
	@Type(() => Number)
	public companyId?: number | null;

	public id?: number;
	public statusUpdatedDatetime?: Date | null;
}

export class SaveIssueRequestDto {
	@ValidateNested()
	@Type(() => IssueDto)
	public issue!: IssueDto;

	@IsOptional()
	@IsArray()
	@IsInt({ each: true })
	@Type(() => Number)
	public clientIds?: Array<ClientIssueInterface['fk_client__id']>;

	@IsOptional()
	@IsArray()
	@IsInt({ each: true })
	@Type(() => Number)
	public vendorIds?: Array<VendorIssueInterface['fk_vendor__id']>;

	// PD-16475 — images uploaded with a NEW issue's description (ignored on an
	// update). Each must be an image the caller uploaded moments before.
	@IsOptional()
	@IsArray()
	@IsInt({ each: true })
	@Type(() => Number)
	public attachmentFileIds?: Array<number>;
}

/**
 * PD-16475 — body of the CRM issue note create/update endpoints. `note` is
 * plain text (older clients, AI tools); `noteHtml` is rich text from the
 * editor, from which the server derives the plain copy. `fileIds` are images
 * uploaded for a NEW note.
 */
export interface SaveIssueNoteRequestModel {
	note?: string;
	noteHtml?: string | null;
	isPinned?: boolean;
	isPortalVisible?: boolean;
	fileIds?: Array<number>;
	/**
	 * PD-16525 — create only: the status a customer-visible reply on a
	 * storefront support request sets (the Conversation tab's "Send as"
	 * choice). Must be a storefront status; the caller needs
	 * `issues:update`. Omitted, the request keeps its status.
	 */
	status?: IssueStatus;
}
